import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchCatalogueTarifs } from '../services/coachingPayment';
import { capturerIncident } from '../services/sentry';
import type { EtatTarifs } from '../services/tarifsCoaching';

/**
 * LECTURE DES TARIFS, AVEC UNE SEULE NOUVELLE TENTATIVE AUTOMATIQUE.
 *
 * L'ÉTAT INDISPONIBLE NE DOIT PAS ÊTRE UN CUL-DE-SAC. Un accroc réseau au premier chargement
 * fermerait sinon le coaching jusqu'à ce que la personne quitte l'écran et y revienne — sans
 * qu'aucun élément ne lui dise que c'est la manœuvre à faire.
 *
 * UNE tentative automatique, puis un geste. Réessayer en boucle transformerait une panne serveur
 * en charge supplémentaire, et masquerait le défaut au lieu de le signaler : au bout de deux
 * échecs, c'est une panne, pas un accroc. L'incident n'est remonté qu'après la seconde — sans quoi
 * chaque micro-coupure réseau produirait du bruit.
 */
export interface TarifsCoaching {
  etat: EtatTarifs;
  reessayer: () => void;
}

export function useTarifsCoaching(): TarifsCoaching {
  const [etat, setEtat] = useState<EtatTarifs>({ statut: 'chargement' });
  const monte = useRef(true);
  const deuxiemeTentative = useRef(false);

  const charger = useCallback(async (estUneNouvelleTentative: boolean) => {
    setEtat({ statut: 'chargement' });
    try {
      const tarifs = await fetchCatalogueTarifs();
      if (monte.current) setEtat({ statut: 'charge', tarifs });
    } catch {
      if (!monte.current) return;
      if (!estUneNouvelleTentative && !deuxiemeTentative.current) {
        deuxiemeTentative.current = true;
        return void charger(true);
      }
      capturerIncident('coaching_tarifs_indisponibles', 'paiement');
      setEtat({ statut: 'indisponible' });
    }
  }, []);

  useEffect(() => {
    monte.current = true;
    void charger(false);
    return () => {
      monte.current = false;
    };
  }, [charger]);

  const reessayer = useCallback(() => {
    // Un geste explicite remet le compteur : la personne a peut-être rétabli sa connexion.
    deuxiemeTentative.current = false;
    void charger(false);
  }, [charger]);

  return { etat, reessayer };
}
